/**
 * 客服工作台的提示音（REQ-2026-574277 §10.3）。
 *
 * 用 WebAudio 现合成一声短"嘀"，不引入音频文件（仓库里没有现成素材，也不想为一声提示音背素材版权）。
 *
 * ⚠️ 浏览器的自动播放限制：AudioContext 在用户**首次交互之前**是 suspended 状态，
 *    这时候调播放**不出声、也不报错**。所以：
 *      1. primeOnFirstInteraction() 在页面上挂一次性的 click/keydown 监听，用户随便点一下就 resume
 *      2. beep() 播之前先看状态，还是 suspended 就直接返回 false —— 调用方据此只闪角标、不指望声音
 *    也就是说"刷新页面后什么都不点"的状态下，**提醒只有角标闪、没有声音**，这是预期行为，不是 bug。
 *
 * 所有方法都不抛异常：提示音是锦上添花，任何情况下都不能把工作台弄挂。
 */

let ctx = null
let primed = false

function ensureContext() {
  if (ctx) return ctx
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext
    if (!Ctor) return null
    ctx = new Ctor()
  } catch (e) {
    ctx = null
  }
  return ctx
}

/** 挂一次性的首次交互监听。可以重复调用，只生效一次。 */
export function primeOnFirstInteraction() {
  if (primed) return
  primed = true
  const resume = () => {
    document.removeEventListener('click', resume, true)
    document.removeEventListener('keydown', resume, true)
    try {
      const c = ensureContext()
      if (c && c.state === 'suspended') c.resume().catch(() => {})
    } catch (e) {
      // ignore
    }
  }
  // capture=true：工作台里有些组件会 stopPropagation，冒泡阶段可能收不到
  document.addEventListener('click', resume, true)
  document.addEventListener('keydown', resume, true)
}

/**
 * 响一声。
 * @returns {boolean} true=真的播了；false=没播（还没交互过 / 浏览器不支持 / 出错）
 */
export function beep() {
  try {
    const c = ensureContext()
    if (!c || c.state !== 'running') return false
    const osc = c.createOscillator()
    const gain = c.createGain()
    osc.type = 'sine'
    osc.frequency.value = 880
    // 起音和收尾都做一小段渐变，否则会有"啪"的一声爆音
    const now = c.currentTime
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(0.2, now + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25)
    osc.connect(gain)
    gain.connect(c.destination)
    osc.start(now)
    osc.stop(now + 0.27)
    return true
  } catch (e) {
    return false
  }
}
